import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, MenuItem, Stack, TextField
} from '@mui/material';
import { useUpdateLicenseMutation, type License } from '@store/api/licenseEndpoints';
import { serverMessage } from '@store/error';
import { useEffect, useState, type FormEvent } from 'react';
import { fieldFrom, type Unit, type ValueField } from './durationField';
import { seatTimeProblem, seatTimeToSave } from './seatTime';

interface SeatTimeDialogProps {
  license?: License;
  onClose: () => void;
}

/**
 * The administrator's own seat time for one licence: how long a seat stays with a device that has
 * stopped calling back before another device may take it.
 *
 * The licence file's value is the least it may be and a week the most; the server gives seats the
 * administrator's value when one is set and the file's otherwise. The app's own "Extend seat
 * allocation" is a separate reservation and is not bounded by any of this.
 */
const SeatTimeDialog = ({ license, onClose }: SeatTimeDialogProps) => {
  const [updateLicense, { isLoading }] = useUpdateLicenseMutation();

  const [field, setField] = useState<ValueField>(fieldFrom(undefined));
  const [error, setError] = useState<string>();

  useEffect(() => {
    setField(fieldFrom(license?.seatAllocationAdminTimeInSeconds));
    setError(undefined);
  }, [license]);

  const floor = license?.seatAllocationTimeInSeconds ?? 0;
  const problem = seatTimeProblem(field, floor);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!license || problem) return;

    setError(undefined);

    try {
      await updateLicense({
        licenseId: license.licenseId,
        seatAllocationAdminTimeInSeconds: seatTimeToSave(field)
      }).unwrap();

      onClose();
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The seat time could not be saved.');
    }
  };

  return (
    <Dialog open={!!license} onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={onSubmit}>
        <DialogTitle>Seat time</DialogTitle>

        <DialogContent>
          <DialogContentText>
            How long a seat stays with a device that has stopped calling back, before another device
            may take it. The license file sets the least this license may have; you can set more
            here, up to 7 days. A longer seat time keeps the seat for a device that is away for a
            while, and leaves fewer seats free for the others.
          </DialogContentText>

          {/* `index.css` zeroes every paragraph's bottom margin, so the space goes above. */}
          <DialogContentText sx={{ mt: 1.5 }}>
            A longer seat time applies at once, also to seats already taken. A shorter one reaches
            each device at its next contact with this server: until then, a seat a device already
            holds keeps the seat time it was given, so to free the seat of a device that is gone,
            release it under Seat sessions. Users can still keep their seat longer from the app with
            Extend seat allocation.
          </DialogContentText>

          <Stack spacing={1} sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={field.own} onChange={(event) => setField({ ...field, own: event.target.checked })} />}
              label={field.own ? 'Seat time, set here' : `Seat time: the license file's ${floor.formatDuration()}`}
            />
            {field.own && (
              <Stack direction='row' spacing={1}>
                <TextField
                  label='Seat time'
                  size='small'
                  type='number'
                  value={field.amount}
                  onChange={(event) => setField({ ...field, amount: event.target.value })}
                  error={!!problem}
                  helperText={problem ?? `At least ${floor.formatDuration()}, the license file's seat time, and at most 7 days.`}
                  slotProps={{ htmlInput: { min: 0, step: 'any' } }}
                  fullWidth
                  autoFocus
                />
                <TextField
                  select
                  size='small'
                  value={field.unit}
                  onChange={(event) => setField({ ...field, unit: event.target.value as Unit })}
                  sx={{ width: 100, flexShrink: 0 }}
                >
                  <MenuItem value='min'>min</MenuItem>
                  <MenuItem value='h'>hours</MenuItem>
                  <MenuItem value='d'>days</MenuItem>
                </TextField>
              </Stack>
            )}
          </Stack>

          {error && <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained' disabled={!license || isLoading || !!problem}>
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SeatTimeDialog;
